import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import moment from 'moment';
import { IoSave } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { applicationAnimVariants } from '../../constants/animationVariants';
import { MINIMUM_LEGAL_AGE } from '../../constants/settings';
import { useAuthContext } from '../../contexts/AuthProvider';
import useApplicationActions from '../../hooks/APIHooks/users/useApplicationActions';
import { saveProfileInfoSchema } from '../../schemas';
import { ApplicationStatus, UpdateAppReqBody } from '../../types/applications';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaDatePicker from '../CommonComps/Inputs/AcademiaDatePicker';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../CommonComps/Inputs/AcademiaTextAreaField';

const ApplicationProfileInfo = ({
  applicationDetails,
}: {
  applicationDetails: ApplicationStatus | null;
}) => {
  // integration of custom API hooks here
  const { updateApplicationHandler } = useApplicationActions();

  // integration of custom hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // function to save application details declared here
  const saveApplicationHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      if (!applicationDetails) return;

      const { user, registration_request } = applicationDetails || {};

      console.log('APPLICATION FORM VALUES: ', values);

      let body: UpdateAppReqBody = {
        user: {
          id: user.id,
          first_name: values.first_name,
          middle_name: values.middle_name || '',
          last_name: values.last_name,
        },
        user_profile: {
          linkedin_profile: values.linkedin_profile || '',
          bio: values.bio,
        },
        registration_request: {
          id: registration_request.id,
          updated_by: user.username,
          is_resubmit:
            registration_request.status === 'resubmit' ? true : false,
          is_view: false,
          user: user.id,
        },
      };

      if (values.date_of_birth) {
        body = {
          ...body,
          user_profile: {
            ...body.user_profile,
            date_of_birth: moment(values.date_of_birth, 'DD-MM-YYYY').format(
              'YYYY-MM-DD'
            ),
          },
        };
      }

      await updateApplicationHandler
        .mutateAsync({
          uuid: applicationDetails.user.uid,
          body,
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('Application Updated!!');

            queryClient.invalidateQueries({
              queryKey: ['current-user-details', currentUser?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ['application-status'],
            });
          } else {
            toast.error(res.message || 'Application Submission Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering application profile info component here
  return (
    <motion.div
      variants={applicationAnimVariants}
      initial='initial'
      animate='animate'
      className='px-5 py-4 shadow-academia-sm rounded-xl'
    >
      <Formik
        initialValues={{
          username: applicationDetails?.user.username || '',
          first_name: applicationDetails?.user.first_name || '',
          middle_name: applicationDetails?.user.middle_name || '',
          last_name: applicationDetails?.user.last_name || '',
          date_of_birth: applicationDetails?.user_profile?.date_of_birth
            ? moment(applicationDetails.user_profile.date_of_birth).format(
                'DD-MM-YYYY'
              )
            : '',
          linkedin_profile:
            applicationDetails?.user_profile?.linkedin_profile || '',
          bio: applicationDetails?.user_profile?.bio || '',
        }}
        enableReinitialize
        onSubmit={saveApplicationHandler}
        validationSchema={saveProfileInfoSchema}
      >
        {(props) => (
          <Form className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5'>
            <div className='space-y-5'>
              <div>
                <AcademiaInputField
                  type='text'
                  name='username'
                  label={`Applicant's Username`}
                  placeholder='Enter your username here...'
                  value={props.values.username}
                  isRequired
                  isDisabled
                />

                {/* {!isUpdatePage ? (
              <AnimatePresence
                initial={false}
                mode='wait'
                onExitComplete={() => null}
              >
                {getCheckAndSuggestUsername.data?.data ? (
                  <motion.label
                    initial={{ y: -20, opacity: 0, display: 'none' }}
                    animate={
                      getCheckAndSuggestUsername.data.message ===
                      'Already Taken'
                        ? { y: 0, opacity: 1, display: 'block' }
                        : {
                            y: -20,
                            opacity: 0,
                            transitionEnd: { display: 'none' },
                          }
                    }
                    htmlFor={`input-user`}
                    className='block mt-1 text-base-red text-sm font-light'
                  >
                    Suggested:
                    {getCheckAndSuggestUsername?.data?.data.map(
                      (suggestion, index) => (
                        <button
                          key={index}
                          type='button'
                          onClick={() => {
                            setUsername(suggestion);
                            setFieldValue('username', suggestion);
                          }}
                          className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                        >
                          <span className='underline'>{suggestion}</span>
                          {index <
                          (getCheckAndSuggestUsername?.data?.data
                            ?.length as number) -
                            1
                            ? ', '
                            : ''}
                        </button>
                      )
                    )}
                    etc.
                  </motion.label>
                ) : null}
              </AnimatePresence>
            ) : null} */}
              </div>

              <AcademiaDatePicker
                name='date_of_birth'
                label={`Applicant's Date of Birth`}
                placeholder='DD-MM-YYYY'
                noInitialDefaultDate
                maxDate={
                  new Date(
                    new Date().setFullYear(
                      new Date().getFullYear() - MINIMUM_LEGAL_AGE
                    )
                  )
                }
                defaultDate={
                  props.values.date_of_birth
                    ? new Date(props.values.date_of_birth)
                    : new Date(
                        new Date().setFullYear(
                          new Date().getFullYear() - MINIMUM_LEGAL_AGE
                        )
                      )
                }
              />

              <AcademiaInputField
                type='text'
                name='linkedin_profile'
                label={`Applicant's Linkedin Profile`}
                placeholder='Enter your linked in profile here...'
              />
            </div>

            <div className='space-y-5'>
              <AcademiaInputField
                type='text'
                name='first_name'
                label={`Applicant's First Name`}
                placeholder='Enter your first name here...'
                isRequired
              />

              <AcademiaInputField
                type='text'
                name='middle_name'
                label={`Applicant's Middle Name`}
                placeholder='Enter your middle name here...'
              />

              <AcademiaInputField
                type='text'
                name='last_name'
                label={`Applicant's Last Name`}
                placeholder='Enter your last name here...'
                isRequired
              />
            </div>

            <div className='md:col-span-2 space-y-6'>
              <AcademiaTextAreaField
                name='bio'
                label={`Applicant's Bio`}
                placeholder='Type your bio here...'
              />

              <div className='flex items-center justify-end'>
                <AcademiaButton
                  type='submit'
                  variant='premium'
                  isLoading={props.isSubmitting}
                  icon={<IoSave className='size-6' />}
                  additionalClassNames='w-full md:w-fit'
                >
                  Save Application
                </AcademiaButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

export default ApplicationProfileInfo;
